import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useI18n, countryName } from '../i18n';

// Country codes with flags - Arab world first, then other major countries
const COUNTRIES = [
  // Arab World
  { code: '+212', country: 'Morocco', flag: '🇲🇦', iso: 'MA' },
  { code: '+213', country: 'Algeria', flag: '🇩🇿', iso: 'DZ' },
  { code: '+216', country: 'Tunisia', flag: '🇹🇳', iso: 'TN' },
  { code: '+20', country: 'Egypt', flag: '🇪🇬', iso: 'EG' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦', iso: 'SA' },
  { code: '+971', country: 'UAE', flag: '🇦🇪', iso: 'AE' },
  { code: '+974', country: 'Qatar', flag: '🇶🇦', iso: 'QA' },
  { code: '+965', country: 'Kuwait', flag: '🇰🇼', iso: 'KW' },
  { code: '+973', country: 'Bahrain', flag: '🇧🇭', iso: 'BH' },
  { code: '+968', country: 'Oman', flag: '🇴🇲', iso: 'OM' },
  { code: '+962', country: 'Jordan', flag: '🇯🇴', iso: 'JO' },
  { code: '+961', country: 'Lebanon', flag: '🇱🇧', iso: 'LB' },
  { code: '+963', country: 'Syria', flag: '🇸🇾', iso: 'SY' },
  { code: '+964', country: 'Iraq', flag: '🇮🇶', iso: 'IQ' },
  { code: '+970', country: 'Palestine', flag: '🇵🇸', iso: 'PS' },
  { code: '+967', country: 'Yemen', flag: '🇾🇪', iso: 'YE' },
  { code: '+218', country: 'Libya', flag: '🇱🇾', iso: 'LY' },
  { code: '+249', country: 'Sudan', flag: '🇸🇩', iso: 'SD' },
  { code: '+222', country: 'Mauritania', flag: '🇲🇷', iso: 'MR' },
  { code: '+253', country: 'Djibouti', flag: '🇩🇯', iso: 'DJ' },
  { code: '+252', country: 'Somalia', flag: '🇸🇴', iso: 'SO' },
  { code: '+269', country: 'Comoros', flag: '🇰🇲', iso: 'KM' },
  // Divider
  { divider: true },
  // Other Major Countries
  { code: '+33', country: 'France', flag: '🇫🇷', iso: 'FR' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸', iso: 'US' },
  { code: '+49', country: 'Germany', flag: '🇩🇪', iso: 'DE' },
  { code: '+34', country: 'Spain', flag: '🇪🇸', iso: 'ES' },
  { code: '+44', country: 'UK', flag: '🇬🇧', iso: 'GB' },
  { code: '+39', country: 'Italy', flag: '🇮🇹', iso: 'IT' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱', iso: 'NL' },
  { code: '+32', country: 'Belgium', flag: '🇧🇪', iso: 'BE' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭', iso: 'CH' },
  { code: '+43', country: 'Austria', flag: '🇦🇹', iso: 'AT' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪', iso: 'SE' },
  { code: '+47', country: 'Norway', flag: '🇳🇴', iso: 'NO' },
  { code: '+45', country: 'Denmark', flag: '🇩🇰', iso: 'DK' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹', iso: 'PT' },
  { code: '+30', country: 'Greece', flag: '🇬🇷', iso: 'GR' },
  { code: '+90', country: 'Turkey', flag: '🇹🇷', iso: 'TR' },
];

// Icons
const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="rtl:-scale-x-100">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const SparklesIcon = () => (
  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
    <path d="M5 3v4" />
    <path d="M19 17v4" />
    <path d="M3 5h4" />
    <path d="M17 19h4" />
  </svg>
);

// Tool icons with colors
const ScissorsIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#9333ea" strokeWidth="2">
    <circle cx="6" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <line x1="20" y1="4" x2="8.12" y2="15.88" />
    <line x1="14.47" y1="14.48" x2="20" y2="20" />
    <line x1="8.12" y1="8.12" x2="12" y2="12" />
  </svg>
);

const ApertureIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="14.31" y1="8" x2="20.05" y2="17.94" />
    <line x1="9.69" y1="8" x2="21.17" y2="8" />
    <line x1="7.38" y1="12" x2="13.12" y2="2.06" />
    <line x1="9.69" y1="16" x2="3.95" y2="6.06" />
    <line x1="14.31" y1="16" x2="2.83" y2="16" />
    <line x1="16.62" y1="12" x2="10.88" y2="21.94" />
  </svg>
);

const FilmIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#eab308" strokeWidth="2">
    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
    <line x1="7" y1="2" x2="7" y2="22" />
    <line x1="17" y1="2" x2="17" y2="22" />
    <line x1="2" y1="12" x2="22" y2="12" />
    <line x1="2" y1="7" x2="7" y2="7" />
    <line x1="2" y1="17" x2="7" y2="17" />
    <line x1="17" y1="17" x2="22" y2="17" />
    <line x1="17" y1="7" x2="22" y2="7" />
  </svg>
);

const MonitorIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const questions = [
  {
    key: 'role',
    question: "What best describes your role?",
    subtitle: "We will personalize your experience based on this.",
    options: [
      { icon: '🎬', label: 'Solo Creator', value: 'creator' },
      { icon: '🏢', label: 'Agency / Studio', value: 'agency' },
      { icon: '💎', label: 'Brand / Business', value: 'business' },
      { icon: '✨', label: 'Other', value: 'other' }
    ]
  },
  {
    key: 'volume',
    question: "How many videos do you subtitle monthly?",
    subtitle: "This helps us ensure enough capacity for your projects.",
    options: [
      { icon: '🌱', label: '1 - 3 videos', value: '1-3' },
      { icon: '🌿', label: '4 - 10 videos', value: '4-10' },
      { icon: '🌳', label: '11 - 25 videos', value: '11-25' },
      { icon: '🔥', label: '25+ videos', value: '25+' }
    ]
  },
  {
    key: 'format',
    question: "What is your primary video format?",
    subtitle: "Short-form content is our specialty.",
    options: [
      { icon: '📱', label: 'Vertical (Shorts/Reels)', value: 'vertical' },
      { icon: '📺', label: 'Horizontal (YouTube)', value: 'horizontal' },
      { icon: '🔄', label: 'Both equally', value: 'both' }
    ]
  },
  {
    key: 'tool',
    question: "Which editing tool do you use?",
    subtitle: "We'll optimize your workflow for your favorite tool.",
    options: [
      { icon: <ScissorsIcon />, label: 'Premiere Pro', value: 'premiere' },
      { icon: <ApertureIcon />, label: 'DaVinci Resolve', value: 'davinci' },
      { icon: <FilmIcon />, label: 'Final Cut Pro', value: 'fcp' },
      { icon: <MonitorIcon />, label: 'Other', value: 'other' }
    ]
  }
];

// Total steps = phone number step (0) + questions length
const PHONE_STEP = 0;
const TOTAL_STEPS = questions.length + 1;
const MAX_PHONE_LENGTH = 15;

const normalizePhoneNumber = (rawValue = '') => {
  const digitsOnly = rawValue.replace(/\D/g, '');
  // Strip leading 0 if present (common in many countries)
  const normalized = digitsOnly.startsWith('0') ? digitsOnly.slice(1) : digitsOnly;
  // Accept any number between 4 and 15 digits
  if (normalized.length >= 4 && normalized.length <= 15) {
    return normalized;
  }
  return null;
};

const OnboardingModal = ({ 
  isOpen, 
  onComplete,
  onPhoneSubmit,
  onSkip,
  isSkippable = true,
  initialAnswers = {} 
}) => {
  const { t, lang, isRTL } = useI18n();
  const [currentStep, setCurrentStep] = useState(PHONE_STEP);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [answers, setAnswers] = useState(initialAnswers);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmittingPhone, setIsSubmittingPhone] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const dropdownRef = useRef(null);
  const countryButtonRef = useRef(null);

  // Detect user's country via geo-IP and set default country code
  useEffect(() => {
    fetch('https://api.country.is/')
      .then((res) => res.json())
      .then((data) => {
        if (data?.country) {
          const match = COUNTRIES.find((c) => !c.divider && c.iso === data.country);
          if (match) setSelectedCountry(match);
        }
      })
      .catch(() => {});
  }, []);

  const currentQuestionIndex = currentStep - 1;
  const currentQ = currentStep > PHONE_STEP ? questions[currentQuestionIndex] : null;
  const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
  const isPhoneValid = Boolean(normalizedPhoneNumber);

  // Safe skip handler
  const handleSkip = useCallback(() => {
    if (isSkippable && onSkip) {
      onSkip();
    }
  }, [isSkippable, onSkip]);

  // Handle escape key to skip (only if skippable)
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleSkip();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleSkip]);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        countryButtonRef.current && !countryButtonRef.current.contains(e.target)
      ) {
        setShowCountryDropdown(false);
        setCountrySearch('');
      }
    };
    if (showCountryDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCountryDropdown]);

  const goToStep = useCallback((step) => {
    setAnimKey(prev => prev + 1);
    setCurrentStep(step);
  }, []);

  // Submit phone number immediately to API, independent from survey
  const handlePhoneSubmit = useCallback(async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (isSubmittingPhone) return;

    if (!isPhoneValid || !normalizedPhoneNumber) {
      setPhoneError(t('Please enter a valid phone number.'));
      return;
    }

    setIsSubmittingPhone(true);
    try {
      // Persist phone in normalized local format (without leading 0)
      const fullPhoneNumber = `${selectedCountry.code} ${normalizedPhoneNumber}`;
      // Submit phone number immediately to API
      if (onPhoneSubmit) {
        await onPhoneSubmit(fullPhoneNumber);
      }
      // Move to survey questions
      goToStep(1);
    } catch (error) {
      console.error('Failed to save phone number:', error);
      // Still proceed to survey even if phone submission fails
      goToStep(1);
    } finally {
      setIsSubmittingPhone(false);
    }
  }, [selectedCountry, onPhoneSubmit, isSubmittingPhone, goToStep, isPhoneValid, normalizedPhoneNumber, t]);

  const handlePhoneChange = useCallback((e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, MAX_PHONE_LENGTH);
    setPhoneNumber(digitsOnly);
    if (phoneError) {
      setPhoneError('');
    }
  }, [phoneError]);

  // Filter countries based on search
  const filteredCountries = countrySearch
    ? COUNTRIES.filter(c => 
        !c.divider && (
          c.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
          countryName(c.iso, c.country, lang).toLowerCase().includes(countrySearch.toLowerCase()) ||
          c.code.includes(countrySearch)
        )
      )
    : COUNTRIES;

  const handleSurveyComplete = useCallback(async (finalAnswers) => {
    setIsSubmitting(true);
    try {
      // Complete onboarding with survey answers (phone was already submitted)
      await onComplete(finalAnswers);
      
      // Track lead conversion in Facebook Pixel
      if (window.fbq) {
        window.fbq('track', 'Lead');
      }
    } catch (error) {
      console.error('Failed to submit onboarding:', error);
      setIsSubmitting(false);
    }
  }, [onComplete]);

  const handleAnswer = useCallback((key, value) => {
    // Update answers first, then handle side effects outside the state updater
    const nextAnswers = { ...answers, [key]: value };
    setAnswers(nextAnswers);

    if (currentQuestionIndex < questions.length - 1) {
      setTimeout(() => goToStep(currentStep + 1), 200);
    } else {
      // Last question - complete the survey (phone already submitted)
      handleSurveyComplete(nextAnswers);
    }
  }, [answers, currentQuestionIndex, currentStep, goToStep, handleSurveyComplete]);

  const handlePrevious = () => {
    if (currentStep > PHONE_STEP) {
      goToStep(currentStep - 1);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 font-sans h-[100dvh]">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl transition-opacity duration-300"
        onClick={isSkippable ? handleSkip : undefined}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-3xl md:rounded-[40px] w-full max-w-xl shadow-2xl overflow-hidden animate-[scaleUp_0.3s_cubic-bezier(0.16,1,0.3,1)] border border-white/50 max-h-[90dvh] flex flex-col">
        
        {/* Progress bar at top */}
        {!isSubmitting && (
          <div className="absolute top-0 start-0 w-full h-1 sm:h-1.5 bg-slate-100 shrink-0 z-20">
            <div
              className="h-full bg-[#f73140] transition-all duration-500 ease-out"
              style={{ width: `${((currentStep + 1) / TOTAL_STEPS) * 100}%` }}
            />
          </div>
        )}

        {/* Skip/Close button (only if skippable) */}
        {isSkippable && !isSubmitting && (
          <button
            onClick={handleSkip}
            className="absolute top-3 end-3 sm:top-4 sm:end-4 md:top-6 md:end-6 z-20 p-1.5 sm:p-2 md:p-2.5 bg-slate-50 hover:bg-slate-100 text-slate-400 hover:text-slate-800 rounded-full transition-all"
          >
            <XIcon />
          </button>
        )}

        {isSubmitting ? (
          // Loading state
          <div className="p-6 sm:p-8 md:p-12 flex flex-col items-center justify-center text-center min-h-[350px] sm:min-h-[450px] animate-[fadeIn_0.5s_ease-out]">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-tr from-[#f73140] to-orange-500 rounded-3xl flex items-center justify-center shadow-xl shadow-[#f73140]/30 mb-6 sm:mb-8 rotate-3">
              <div className="text-white animate-spin">
                <SparklesIcon />
              </div>
            </div>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-3 px-4">{t('Setting up your experience...')}</h2>
            <p className="text-sm sm:text-base text-slate-400 font-semibold px-4">{t('Just a moment while we personalize ArabCap for you.')}</p>
          </div>
        ) : currentStep === PHONE_STEP ? (
          // Phone number step
          <div key={`phone-${animKey}`} className="p-4 sm:p-6 md:p-10 flex flex-col overflow-y-auto animate-[slideContent_0.3s_ease-out]">
            {/* Icon */}
            <div className="w-12 h-12 md:w-16 md:h-16 mx-auto bg-[#f73140]/5 rounded-2xl flex items-center justify-center text-[#f73140] mb-4 md:mb-5 ring-1 ring-[#f73140]/10 shadow-inner shrink-0">
              <PhoneIcon />
            </div>
            
            <div className="text-center mb-5 md:mb-8">
              <h2 className="text-lg sm:text-xl md:text-2xl font-extrabold text-slate-900 tracking-tight mb-2">{t("What's your phone number?")}</h2>
              <p className="text-xs sm:text-sm text-slate-400">{t('Step {n} of {total}', { n: 1, total: TOTAL_STEPS })}</p>
            </div>

            <form onSubmit={handlePhoneSubmit} className="space-y-3 sm:space-y-4">
              <div>
                {/* Country Code Selector + Phone Input */}
                <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                  {/* Country Selector */}
                  <div className="relative w-full sm:w-auto shrink-0">
                    <button
                      ref={countryButtonRef}
                      type="button"
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      disabled={isSubmittingPhone}
                      className="w-full sm:w-auto h-full px-2 sm:px-3 py-3 sm:py-4 bg-slate-50 border-2 border-slate-200 rounded-xl sm:rounded-2xl hover:bg-white focus:outline-none focus:border-[#f73140] focus:ring-4 focus:ring-[#f73140]/10 transition-all disabled:opacity-60 flex items-center justify-between sm:justify-start gap-1 sm:gap-2 min-w-[100px] sm:min-w-[120px]"
                    >
                      <span className="text-xl sm:text-2xl">{selectedCountry.flag}</span>
                      <span className="font-bold text-slate-700 text-xs sm:text-sm" dir="ltr">{selectedCountry.code}</span>
                      <ChevronDownIcon />
                    </button>

                    {/* Dropdown - rendered as portal to avoid modal overflow clipping */}
                    {showCountryDropdown && createPortal(
                      <div
                        ref={dropdownRef}
                        className="fixed z-[10000] bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[60vh] overflow-hidden flex flex-col"
                        style={{
                          width: Math.max(countryButtonRef.current?.offsetWidth || 288, 288),
                          top: (countryButtonRef.current?.getBoundingClientRect().bottom || 200) + 8,
                          ...(isRTL
                            ? { right: window.innerWidth - (countryButtonRef.current?.getBoundingClientRect().right || window.innerWidth) }
                            : { left: countryButtonRef.current?.getBoundingClientRect().left || 0 }),
                        }}
                      >
                        {/* Search */}
                        <div className="p-3 border-b border-slate-100 sticky top-0 bg-white">
                          <input
                            type="text"
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            placeholder={t('Search country...')}
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#f73140] focus:ring-2 focus:ring-[#f73140]/10"
                            autoFocus
                          />
                        </div>

                        {/* Country List */}
                        <div className="overflow-y-auto">
                          {filteredCountries.map((country, idx) =>
                            country.divider ? (
                              <div key={`divider-${idx}`} className="relative my-2">
                                <div className="border-t-2 border-slate-200" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <span className="px-3 bg-white text-xs font-bold text-slate-400 uppercase tracking-wider">{t('Other Countries')}</span>
                                </div>
                              </div>
                            ) : (
                              <button
                                key={country.iso}
                                type="button"
                                onClick={() => {
                                  setSelectedCountry(country);
                                  setShowCountryDropdown(false);
                                  setCountrySearch('');
                                }}
                                className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors text-start ${
                                  selectedCountry.iso === country.iso ? 'bg-[#f73140]/5' : ''
                                }`}
                              >
                                <span className="text-2xl">{country.flag}</span>
                                <span className="flex-1 font-medium text-slate-700 text-sm">{countryName(country.iso, country.country, lang)}</span>
                                <span className="font-bold text-slate-500 text-sm" dir="ltr">{country.code}</span>
                                {selectedCountry.iso === country.iso && (
                                  <div className="text-[#f73140]">
                                    <CheckIcon />
                                  </div>
                                )}
                              </button>
                            )
                          )}
                        </div>
                      </div>,
                      document.body
                    )}
                  </div>

                  {/* Phone Number Input */}
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder={t('Phone number')}
                    required
                    autoFocus
                    disabled={isSubmittingPhone}
                    inputMode="numeric"
                    maxLength={MAX_PHONE_LENGTH}
                    className={`w-full min-w-0 flex-1 px-3 sm:px-5 py-3 sm:py-4 bg-slate-50 border-2 rounded-xl sm:rounded-2xl text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-4 focus:ring-[#f73140]/10 transition-all font-medium text-base sm:text-lg disabled:opacity-60 ${
                      phoneError ? 'border-red-400 focus:border-red-500 focus:ring-red-100' : 'border-slate-200 focus:border-[#f73140]'
                    }`}
                  />
                </div>

                {phoneError && (
                  <p className="mt-2 px-1 text-xs sm:text-sm text-red-600 font-medium">
                    {phoneError}
                  </p>
                )}

                <div className="flex items-center gap-2 mt-2 sm:mt-3 px-1">
                  <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-[#f73140]/10 flex items-center justify-center shrink-0">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#f73140" strokeWidth="2" className="sm:w-3 sm:h-3">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                      <circle cx="9" cy="7" r="4" />
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                    </svg>
                  </div>
                  <p className="text-xs sm:text-sm text-slate-500 font-medium">
                    {t('Join our community and receive exclusive offers')}
                  </p>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={!isPhoneValid || isSubmittingPhone}
                className={`w-full px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl text-sm sm:text-base font-bold transition-all flex items-center justify-center gap-2 ${
                  isPhoneValid && !isSubmittingPhone
                    ? 'bg-[#f73140] text-white shadow-lg shadow-[#f73140]/20 hover:bg-[#d62331] hover:shadow-xl hover:shadow-[#f73140]/25'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
              >
                {isSubmittingPhone ? (
                  <>
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('Saving...')}
                  </>
                ) : (
                  <>
                    {t('Continue')} <ArrowRightIcon />
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-xs text-slate-400 mt-4 sm:mt-6">
              {t("We'll never share your number with third parties")}
            </p>
          </div>
        ) : currentQ ? (
          // Question steps
          <div key={`question-${animKey}`} className="p-4 sm:p-6 md:p-10 flex flex-col overflow-y-auto animate-[slideContent_0.3s_ease-out]">
            {/* Back button - only show if not on first question */}
            <button 
              onClick={handlePrevious}
              className="absolute top-3 start-3 sm:top-4 sm:start-4 md:top-6 md:start-6 text-slate-400 hover:text-slate-700 flex items-center gap-1 text-xs sm:text-sm font-bold transition-all hover:gap-2"
            >
              <ChevronLeftIcon /> {t('Back')}
            </button>

            <div className="text-center mb-4 sm:mb-6 pt-4">
              <h2 className="text-lg sm:text-xl md:text-2xl font-extrabold text-slate-900 tracking-tight mb-2 px-2">
                {t(currentQ.question)}
              </h2>
              <p className="text-xs sm:text-sm text-slate-400">{t('Step {n} of {total}', { n: currentStep + 1, total: TOTAL_STEPS })}</p>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
              {currentQ.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(currentQ.key, option.value)}
                  className={`p-3 sm:p-4 md:p-5 rounded-xl sm:rounded-2xl border-2 text-start transition-all duration-200 flex flex-col gap-1 sm:gap-2 relative overflow-hidden group hover:scale-[1.02] ${
                    answers[currentQ.key] === option.value 
                      ? 'border-[#f73140] bg-[#f73140]/5 shadow-lg shadow-[#f73140]/10' 
                      : 'border-slate-100 bg-white hover:border-[#f73140]/30 hover:bg-slate-50 hover:shadow-md'
                  }`}
                >
                  <div className="text-xl sm:text-2xl md:text-3xl mb-0.5 sm:mb-1 transition-transform duration-300 group-hover:scale-110 group-hover:-translate-y-0.5 origin-left rtl:origin-right">
                    {option.icon}
                  </div>
                  <span className={`font-bold text-xs sm:text-sm md:text-base ${
                    answers[currentQ.key] === option.value ? 'text-[#f73140]' : 'text-slate-700'
                  }`}>
                    {t(option.label)}
                  </span>
                  {answers[currentQ.key] === option.value && (
                    <div className="absolute top-2 end-2 sm:top-3 sm:end-3 w-5 h-5 sm:w-6 sm:h-6 bg-[#f73140] rounded-full flex items-center justify-center text-white">
                      <CheckIcon />
                    </div>
                  )}
                </button>
              ))}
            </div>

            {/* Subtitle hint */}
            <p className="text-center text-xs text-slate-400 mt-4 sm:mt-6 px-2">
              {t(currentQ.subtitle)}
            </p>
          </div>
        ) : null}
      </div>

      <style>{`
        @keyframes scaleUp {
          from { opacity: 0; transform: scale(0.95) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideContent {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default OnboardingModal;
